/**
 * Curated word lists used by the achievement analyzer to detect
 * pedagogically-meaningful writing moves.
 *
 * These aren't exhaustive — they're "if a kid uses this, they're
 * deliberately reaching for craft" lists. False negatives are fine
 * (missing a celebration is cheap); false positives are worse
 * (cheapens the moment).
 */

/** Words that signal the writer used a sense — saw, heard, smelled, etc.
 *  Hits "Show, Don't Tell" pedagogy: writers ground readers in the senses. */
export const SENSORY_WORDS = new Set([
  // sight
  'saw', 'watched', 'looked', 'looked', 'glimpsed', 'noticed', 'spotted',
  'peered', 'gazed', 'observed', 'stared', 'glanced', 'glittered',
  'shone', 'sparkled', 'glowed', 'flashed', 'gleamed',
  // sound
  'heard', 'listened', 'sounded', 'echoed', 'whispered', 'shouted',
  'rang', 'creaked', 'thundered', 'rumbled', 'hummed', 'buzzed', 'snapped',
  // smell
  'smelled', 'scented', 'sniffed', 'smelt', 'reeked',
  // touch
  'felt', 'touched', 'brushed', 'stroked', 'gripped', 'grasped',
  'rough', 'smooth', 'silky', 'sticky', 'soft', 'warm', 'cold', 'icy', 'hot',
  // taste
  'tasted', 'sweet', 'bitter', 'sour', 'salty', 'spicy', 'tangy',
]);

/** Strong, specific verbs — alternatives to was/got/did/went.
 *  Hits "Word Craft" pedagogy: writers pick verbs that show motion + feeling. */
export const VIVID_VERBS = new Set([
  'raced', 'sprinted', 'dashed', 'charged', 'hurried', 'sped', 'darted', 'zoomed',
  'crept', 'snuck', 'tiptoed', 'slipped', 'crawled', 'wriggled',
  'whispered', 'shouted', 'hollered', 'called', 'yelled', 'murmured', 'mumbled',
  'laughed', 'giggled', 'chuckled', 'cackled', 'snorted', 'snickered',
  'tumbled', 'crashed', 'collapsed', 'plummeted',
  'leaped', 'jumped', 'bounded', 'skipped', 'hopped', 'sprang',
  'hugged', 'grabbed', 'snatched', 'clutched', 'seized',
  'wondered', 'imagined', 'considered', 'decided', 'realized', 'discovered',
  'glared', 'beamed', 'frowned', 'grinned', 'scowled', 'smirked',
  'stomped', 'marched', 'wandered', 'shuffled', 'staggered',
  'shoved', 'tossed', 'flung', 'hurled', 'launched',
]);

/** Feeling words beyond happy/sad — emotional vocabulary.
 *  Hits "Word Craft" + voice pedagogy. */
export const FEELING_WORDS = new Set([
  'excited', 'thrilled', 'delighted', 'proud', 'joyful', 'overjoyed', 'elated',
  'nervous', 'anxious', 'worried', 'scared', 'terrified', 'frightened', 'startled',
  'angry', 'furious', 'frustrated', 'irritated', 'annoyed', 'enraged',
  'curious', 'surprised', 'amazed', 'astonished', 'shocked', 'stunned',
  'disappointed', 'lonely', 'ashamed', 'embarrassed', 'guilty', 'hurt',
  'peaceful', 'calm', 'content', 'satisfied', 'relieved', 'grateful',
  'jealous', 'envious', 'confused', 'puzzled',
  'brave', 'confident', 'determined', 'hopeful',
]);

/** Setting markers — when + where. Establishes a scene.
 *  Hits "Story Craft" pedagogy. */
export const SETTING_PHRASES: RegExp[] = [
  /\b(yesterday|today|tomorrow)\b/i,
  /\blast (week|year|night|summer|winter|fall|spring|month)\b/i,
  /\bnext (week|year|summer|winter|month)\b/i,
  /\bone (day|morning|night|evening|afternoon|time)\b/i,
  /\bin the (morning|afternoon|evening|night|summer|winter)\b/i,
  /\bat (school|home|the park|the store|the beach|the library|night|noon|dawn|dusk)\b/i,
  /\b(once|long ago|far away|years ago|when i was)\b/i,
];

/** Transition words — signal logical structure between ideas. */
export const TRANSITION_WORDS = new Set([
  'first', 'second', 'third', 'next', 'then', 'after', 'later', 'finally', 'lastly',
  'also', 'furthermore', 'moreover', 'similarly', 'additionally',
  'meanwhile', 'eventually', 'soon', 'suddenly', 'immediately',
  'before', 'afterward', 'previously',
]);

/** Words that introduce a counterview — sophisticated writers consider both sides. */
export const COUNTERVIEW_MARKERS: RegExp[] = [
  /\bhowever\b/i,
  /\bon the other hand\b/i,
  /\balthough\b/i,
  /\bdespite\b/i,
  /\beven though\b/i,
  /\b(but|yet) (i|you|we|they|he|she|some|others)\b/i, // "but I think otherwise"
];

/** Opinion + reasoning pattern — claim backed by because. */
export const CLAIM_WITH_REASON_REGEX =
  /\b(i (think|believe|feel) [^.!?]{1,60}because)\b/i;

/** Causal connection — "because" used in a sentence. */
export const CAUSAL_REGEX = /\bbecause\b/i;

/** Example connector — "for example" or "for instance." */
export const EXAMPLE_CONNECTOR_REGEX = /\b(for example|for instance|such as)\b/i;

/** First-person markers. */
export const FIRST_PERSON_REGEX = /\b(i|my|me|i'm|i've|i'll|i'd|mine)\b/i;

/** Second-person markers — addressing the reader. */
export const SECOND_PERSON_REGEX = /\b(you|your|you're|you've|you'll|you'd)\b/i;

/** "I wonder…" — a real question that shows curiosity. */
export const WONDER_REGEX = /\bi wonder\b/i;

/** Simile detector — "like a/an/the X" or "as X as Y." */
export const SIMILE_REGEX = /\b(like (a|an|the) \w+|as \w+ as)\b/i;

/** Dialogue — text inside quotation marks (straight or curly). */
export const DIALOGUE_REGEX = /["“][^"”]{2,}["”]/;

/** Proper noun heuristic — capitalized word that isn't at sentence start.
 *  Catches names, places. Crude but useful. */
export const PROPER_NOUN_REGEX = /(?<=[a-z0-9,;:'"\s])\s+([A-Z][a-z]{2,})/g;

/** Number in text — digit OR number word. */
export const NUMBER_REGEX =
  /\b(\d+|one|two|three|four|five|six|seven|eight|nine|ten|hundred|thousand)\b/i;

/** Specific measurement — number followed by a common unit. */
export const MEASUREMENT_REGEX =
  /\b\d+\s*(minutes?|hours?|seconds?|days?|years?|miles?|feet|inches?|pounds?|dollars?|cents?|degrees?|miles per hour)\b/i;
